import { useRef, useState, type FormEvent } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { mapFieldErrors } from '@/api/fieldErrors';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';
import { SubmitButton } from '@/components/SubmitButton';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useFreshOnOpen } from '@/components/useFreshOnOpen';

export interface VoidReferralDialogProps {
  open: boolean;
  onClose: () => void;
  onVoid: (reason: string) => Promise<void>;
}

const FIELDS = ['reason'] as const;

export function VoidReferralDialog(props: VoidReferralDialogProps) {
  // A fresh form on every open (TMMRSL-0071).
  return <VoidReferralForm key={useFreshOnOpen(props.open)} {...props} />;
}

function VoidReferralForm({ open, onClose, onVoid }: VoidReferralDialogProps) {
  const { t } = useTranslation();
  const describe = useActionError();
  const reasonRef = useRef<HTMLInputElement>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  const fields = useFieldErrors(FIELDS, { reason: reasonRef }, submitting);

  function handleClose() {
    if (submitting) return;
    onClose();
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setError(null);
      fields.fail({ reason: t('validation.reasonVoid') });
      return;
    }
    setSubmitting(true);
    setError(null);
    fields.reset();
    try {
      await onVoid(trimmed);
      onClose();
    } catch (err) {
      const action = describe(err);
      setError(action);
      fields.failMarked(mapFieldErrors(action.fieldKeys, { reason: 'reason' }), {
        reason: t('affiliates.field.reason'),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : handleClose} fullWidth maxWidth="sm">
      <Box component="form" noValidate onSubmit={(e) => void handleSubmit(e)}>
        <DialogTitle>{t('affiliates.void.title')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{t('affiliates.void.body')}</Typography>
          <ActionErrorAlert error={error} sx={{ mb: 2 }} />
          <TextField
            required
            fullWidth
            margin="dense"
            label={t('affiliates.void.reason')}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              fields.clear('reason');
            }}
            inputRef={reasonRef}
            error={fields.errors.reason !== undefined}
            helperText={fields.errors.reason}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" color="error" loading={submitting}>
            {t('affiliates.void.confirm')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
